import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Share2, Send, Loader2, CheckCircle2, Linkedin, Instagram, Globe, FileText, Plus, Clock, ExternalLink, AlertCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_ICONS = {
  linkedin_company: Linkedin,
  linkedin_personal: Linkedin,
  instagram: Instagram,
  facebook: Globe,
  twitter: Share2,
};

const PLATFORM_COLORS = {
  linkedin_company: 'bg-blue-600',
  linkedin_personal: 'bg-indigo-600',
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  facebook: 'bg-blue-500',
  twitter: 'bg-slate-900',
};

function SocialPublisher() {
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [error, setError] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [postText, setPostText] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState([]);

  const loadProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    setError('');
    try {
      const res = await api.get('/social-profiles');
      const activeProfiles = (res.data || []).filter(p => p.active);
      setProfiles(activeProfiles);
      if (activeProfiles.length === 0) {
        setError('no_profiles');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel caricamento dei profili');
    } finally {
      setLoadingProfiles(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/buffer/history');
      setHistory(res.data.posts || []);
    } catch {}
  }, []);

  useEffect(() => { loadProfiles(); loadHistory(); }, [loadProfiles, loadHistory]);

  const toggleProfile = (profileId) => {
    setSelectedProfiles(prev =>
      prev.includes(profileId) ? prev.filter(p => p !== profileId) : [...prev, profileId]
    );
  };

  const handlePublish = async (publishNow) => {
    if (!postText.trim()) return toast.error('Scrivi il contenuto del post');
    if (!selectedProfiles.length) return toast.error('Seleziona almeno un profilo');

    // Get Buffer channel IDs from selected profiles
    const channelIds = profiles
      .filter(p => selectedProfiles.includes(p.profile_id) && p.buffer_profile_id)
      .map(p => p.buffer_profile_id);

    const noBufferProfiles = profiles
      .filter(p => selectedProfiles.includes(p.profile_id) && !p.buffer_profile_id);

    if (noBufferProfiles.length > 0) {
      const names = noBufferProfiles.map(p => p.name).join(', ');
      toast.warning(`${names}: non collegato/i a Buffer. Vai su Profili Social per associare un canale.`);
    }

    if (channelIds.length === 0) {
      return toast.error('Nessun profilo selezionato ha un canale Buffer associato');
    }

    setPublishing(true);
    try {
      const res = await api.post('/buffer/publish', {
        text: postText,
        channel_ids: channelIds,
        now: publishNow,
      });
      toast.success(res.data.message);
      setPostText('');
      setSelectedProfiles([]);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setPublishing(false);
    }
  };

  if (loadingProfiles) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-[#1A3A5C]" /></div>;
  }

  if (error === 'no_profiles') {
    return (
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-8 text-center space-y-3">
          <Share2 className="w-10 h-10 text-[#94A3B8] mx-auto" />
          <p className="text-sm text-[#1A3A5C] font-medium">Nessun profilo social configurato</p>
          <p className="text-xs text-[#94A3B8]">Crea i tuoi profili social e collegali a Buffer per iniziare a pubblicare.</p>
          <Button size="sm" onClick={() => window.location.href = '/social-profiles'} className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="goto-profiles-btn">
            <Settings className="w-3.5 h-3.5 mr-1.5" /> Configura profili
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-6 text-center space-y-3">
          <AlertCircle className="w-8 h-8 text-amber-600 mx-auto" />
          <p className="text-sm text-amber-800">{error}</p>
          <Button size="sm" variant="outline" onClick={loadProfiles}>Riprova</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-publisher">
      {/* Profile selection */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-xs font-semibold text-[#1A3A5C]">Seleziona profili</Label>
          <Button variant="link" size="sm" className="text-xs text-[#2E75B6] h-auto p-0" onClick={() => window.location.href = '/social-profiles'}>
            Gestisci profili
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {profiles.map(p => {
            const selected = selectedProfiles.includes(p.profile_id);
            const PIcon = PLATFORM_ICONS[p.platform] || Globe;
            const bgColor = PLATFORM_COLORS[p.platform] || 'bg-slate-600';
            return (
              <button
                key={p.profile_id}
                onClick={() => toggleProfile(p.profile_id)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  selected
                    ? 'border-[#2E75B6] bg-[#2E75B6]/5 shadow-sm'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'
                }`}
                data-testid={`pub-profile-${p.profile_id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 ${bgColor}`}>
                  <PIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[#1A3A5C] truncate">{p.name}</p>
                  <p className="text-[10px] text-[#94A3B8]">{p.platform_label || p.platform}</p>
                  {!p.buffer_profile_id && <p className="text-[9px] text-amber-500 mt-0.5">Buffer non collegato</p>}
                </div>
                {selected && <CheckCircle2 className="w-4 h-4 text-[#2E75B6] shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Post composer */}
      <div>
        <Label className="text-xs font-semibold text-[#1A3A5C] mb-2 block">Contenuto del post</Label>
        <textarea
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
          placeholder="Scrivi il tuo post..."
          rows={4}
          className="w-full rounded-lg border border-[#E2E8F0] px-4 py-3 text-sm text-[#1A3A5C] placeholder:text-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 resize-none"
          data-testid="buffer-post-text"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-[#94A3B8]">{postText.length} caratteri — {selectedProfiles.length} profilo/i selezionato/i</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handlePublish(false)} disabled={publishing} data-testid="buffer-schedule-btn">
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Accoda
            </Button>
            <Button size="sm" className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" onClick={() => handlePublish(true)} disabled={publishing} data-testid="buffer-publish-btn">
              {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Pubblica ora
            </Button>
          </div>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <Label className="text-xs font-semibold text-[#1A3A5C] mb-2 block">Storico post</Label>
          <div className="space-y-2">
            {history.slice(0, 5).map(post => (
              <div key={post.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg bg-white">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-[#1A3A5C] line-clamp-1">{post.text}</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">{new Date(post.created_at).toLocaleString('it-CH')}</p>
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0 ml-3">{post.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BeehiivPublisher() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/beehiiv/posts');
      setPosts(res.data.posts || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore nel caricamento dei post Beehiiv';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleCreate = async (status) => {
    if (!title.trim()) return toast.error('Il titolo e obbligatorio');
    if (!content.trim()) return toast.error('Il contenuto e obbligatorio');

    setCreating(true);
    try {
      const res = await api.post('/beehiiv/posts', { title, subtitle, content, status });
      toast.success(res.data.message);
      setTitle('');
      setSubtitle('');
      setContent('');
      setShowForm(false);
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-[#1A3A5C]" /></div>;

  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-6 text-center space-y-3">
          <AlertCircle className="w-8 h-8 text-amber-600 mx-auto" />
          <p className="text-sm text-amber-800">{error}</p>
          <div className="flex justify-center gap-2">
            <Button size="sm" variant="outline" onClick={loadPosts}>Riprova</Button>
            <Button size="sm" onClick={() => window.location.href = '/settings'}>Configura Beehiiv</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="beehiiv-publisher">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#64748B]">{posts.length} newsletter trovate</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="beehiiv-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuova newsletter
        </Button>
      </div>

      {showForm && (
        <Card className="border-[#2E75B6]/30 bg-[#2E75B6]/5">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-xs text-[#1A3A5C] font-medium">Titolo *</Label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo della newsletter" className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="beehiiv-title" />
            </div>
            <div>
              <Label className="text-xs text-[#1A3A5C] font-medium">Sottotitolo</Label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Sottotitolo (opzionale)" className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="beehiiv-subtitle" />
            </div>
            <div>
              <Label className="text-xs text-[#1A3A5C] font-medium">Contenuto * (HTML supportato)</Label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={"<h2>Ciao!</h2>\n<p>Scrivi il contenuto della newsletter...</p>"}
                rows={8}
                className="w-full mt-1 rounded-lg border border-[#E2E8F0] px-4 py-3 text-sm text-[#1A3A5C] placeholder:text-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 resize-none font-mono"
                data-testid="beehiiv-content"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => handleCreate('draft')} disabled={creating} data-testid="beehiiv-save-draft">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Salva bozza
              </Button>
              <Button size="sm" className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" onClick={() => handleCreate('published')} disabled={creating} data-testid="beehiiv-publish-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Pubblica
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Annulla</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {posts.map(post => (
          <div key={post.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg bg-white hover:shadow-sm transition-shadow" data-testid={`beehiiv-post-${post.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#1A3A5C]">{post.title || 'Senza titolo'}</p>
              {post.subtitle && <p className="text-[10px] text-[#94A3B8] mt-0.5">{post.subtitle}</p>}
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[10px] text-[#CBD5E1]">{post.displayed_date || '-'}</span>
                {post.audience && <Badge variant="outline" className="text-[9px]">{post.audience}</Badge>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <Badge className={post.status === 'confirmed' || post.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                {post.status === 'confirmed' ? 'Pubblicata' : post.status === 'draft' ? 'Bozza' : post.status}
              </Badge>
              {post.web_url && (
                <a href={post.web_url} target="_blank" rel="noopener noreferrer" className="text-[#2E75B6] hover:text-[#1A3A5C]">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        ))}
        {posts.length === 0 && !showForm && (
          <p className="text-sm text-[#94A3B8] text-center py-6">Nessuna newsletter. Clicca "Nuova newsletter" per iniziare.</p>
        )}
      </div>
    </div>
  );
}

export default function PublishingPage({ embedded = false }) {
  return (
    <div className="animate-fade-in" data-testid="publishing-page">
      {!embedded && (
        <div className="page-header mb-4">
          <h1 className="page-title">Pubblicazione</h1>
          <p className="page-subtitle">Pubblica sui social media via Buffer e crea newsletter via Beehiiv.</p>
        </div>
      )}

      <Tabs defaultValue="social">
        <TabsList className="bg-white border border-[#E2E8F0] mb-6">
          <TabsTrigger value="social" data-testid="tab-social" className="flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" /> Social Media
          </TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Newsletter
          </TabsTrigger>
        </TabsList>
        <TabsContent value="social"><SocialPublisher /></TabsContent>
        <TabsContent value="newsletter"><BeehiivPublisher /></TabsContent>
      </Tabs>
    </div>
  );
}
